import styled from 'styled-components';

import theme from '../../config/theme';

// ---------------------------------------------------------------------------
// Cobranças — primitivos do redesign (design handoff 2026-08-06).
//
// A tela deixou de ser "grid de filtros + tabela de 10 colunas" e passou a ser
// dois cards brancos: filtros com hierarquia de 4 níveis e resultados em
// linha-cartão de 2 níveis (o texto não quebra mais em 2-4 linhas).
//
// Tipografia: a escala do design (tamanhos/pesos/tabular-nums) foi recriada
// sobre a fonte atual do app (Noto Sans). Adotar Inter é decisão global, não
// carona deste rework.
//
// Estes primitivos são LOCAIS por enquanto. Se outra tela reusar o card branco
// ou a linha-cartão, promover para styles/ui/ em vez de copiar (FC7 H.4).
// ---------------------------------------------------------------------------

const BORDER = 'rgba(0,0,0,0.08)';
const BORDER_STRONG = 'rgba(0,0,0,0.12)';
const DIVIDER = 'rgba(0,0,0,0.07)';

export const fadeIn = 'mwaFade';

// ─── Card branco (base das duas seções) ──────────────────────────────────────
export const Card = styled.section`
   background: #fff;
   border: 1px solid ${BORDER};
   border-radius: 12px;
   box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

   /* SEM overflow:hidden de propósito. O card precisa deixar popovers
      (dropdown de empresa emissora) escaparem dos seus limites — com o recorte
      ligado, o painel era cortado na borda inferior do card de filtros.
      O arredondamento dos cantos, que era o motivo do recorte existir, é
      resolvido no próprio CardFoot (o único filho com fundo). */

   @keyframes ${fadeIn} {
      from {
         opacity: 0;
         transform: translateY(4px);
      }
      to {
         opacity: 1;
         transform: translateY(0);
      }
   }
`;

export const CardHead = styled.div`
   display: flex;
   align-items: center;
   gap: 12px;
   flex-wrap: wrap;
   padding: 14px 16px;
   border-bottom: 1px solid ${DIVIDER};
`;

export const CardTitle = styled.h2`
   margin: 0;
   font-size: 15px;
   font-weight: 700;
   display: inline-flex;
   align-items: center;
   gap: 8px;
   color: #111;
`;

export const CardBody = styled.div`
   padding: 16px;
`;

export const CardFoot = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   flex-wrap: wrap;
   padding: 12px 16px;
   background: #fafafa;
   border-top: 1px solid ${DIVIDER};
   /* Acompanha o raio do card: é o que substitui o overflow:hidden removido. */
   border-radius: 0 0 12px 12px;
`;

export const Spacer = styled.span`
   flex: 1;
`;

// ─── Grupos internos de filtro (1.3 / 1.4) ───────────────────────────────────
export const FilterGroup = styled.div`
   border: 1px solid ${BORDER};
   border-radius: 12px;
   padding: 14px;
   background: #fcfcfd;
`;

export const GroupLabel = styled.div`
   font-size: 11px;
   font-weight: 800;
   color: #8a8a8a;
   text-transform: uppercase;
   letter-spacing: 0.06em;
   margin-bottom: 10px;
   display: flex;
   align-items: baseline;
   gap: 8px;

   .hint {
      font-size: 11px;
      font-weight: 600;
      text-transform: none;
      letter-spacing: 0;
      color: #9a9a9a;
   }
`;

export const FieldLabel = styled.label`
   display: block;
   font-size: 12px;
   font-weight: 700;
   color: #666;
   margin-bottom: 6px;
`;

// Label dos campos de busca primária — caixa alta, mais destaque.
export const PrimaryFieldLabel = styled.label`
   display: block;
   font-size: 11px;
   font-weight: 800;
   color: #8a8a8a;
   text-transform: uppercase;
   letter-spacing: 0.06em;
   margin-bottom: 6px;
`;

const controlBase = `
   width: 100%;
   height: 40px;
   border-radius: 10px;
   border: 1px solid ${BORDER_STRONG};
   background: #fff;
   color: #111;
   font-family: inherit;
   font-size: 14px;
   padding: 0 12px;
   box-sizing: border-box;

   &:focus {
      outline: none;
      border-color: #4098ef;
      box-shadow: 0 0 0 3px rgba(64, 152, 239, 0.18);
   }
`;

export const TextInput = styled.input`
   ${controlBase}
   ${({ $withIcon }) => ($withIcon ? 'padding-left: 38px;' : '')}

   &::placeholder {
      color: #9a9a9a;
   }
`;

export const SelectInput = styled.select`
   ${controlBase}
   cursor: pointer;
`;

export const SearchFieldWrap = styled.div`
   position: relative;

   .icon {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #9a9a9a;
      display: inline-flex;
      pointer-events: none;
   }
`;

// ─── Grids ───────────────────────────────────────────────────────────────────
export const PrimaryGrid = styled.div`
   display: grid;
   grid-template-columns: minmax(260px, 2.2fr) minmax(150px, 1fr);
   gap: 16px;
   align-items: end;

   @media (max-width: 720px) {
      grid-template-columns: 1fr;
   }
`;

export const AdvancedGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
   gap: 14px;
   margin-top: 14px;
   animation: ${fadeIn} 0.16s ease;
`;

export const ClassificationGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
   gap: 12px;
`;

// PresetRow/PresetChip saíram em 2026-09-02 junto com os presets de
// vencimento. VDivider fica: a paginação usa.
export const VDivider = styled.span`
   width: 1px;
   height: 40px;
   background: ${BORDER};
`;

export const DateRow = styled.div`
   display: flex;
   align-items: end;
   gap: 12px;

   /* Inputs dividem a largura do grupo (o TextInput já é width:100%). O piso
      de 120px é o suficiente pro type="date" pt-BR sem clipar. */
   > div {
      flex: 1;
      min-width: 120px;
   }
`;

// Linha dos três ranges de data (Vencimento/Recebimento/Emissão, 0106).
// auto-fit como o AdvancedGrid: os três cabem lado a lado no desktop e
// quebram por coluna em tela estreita — sem grupo sobrando sozinho numa
// segunda linha por causa de larguras fixas.
export const DateGroupsRow = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
   gap: 12px;
`;

// Erro de formato apontado pelo backend (422 com field, 0106) — inline sob o
// input, política FC4 (validação com campo nunca vira toast).
export const FieldErrorText = styled.span`
   display: block;
   margin-top: 4px;
   font-size: 12px;
   font-weight: 600;
   color: ${theme.colors.danger};
`;

// Pílula de "filtro ativo" no cabeçalho do card, com × individual.
export const ActiveChip = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 3px 4px 3px 10px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 700;
   color: #0b3a63;
   background: rgba(64, 152, 239, 0.14);
   border: 1px solid rgba(64, 152, 239, 0.28);

   button {
      width: 16px;
      height: 16px;
      border: 0;
      border-radius: 999px;
      background: rgba(11, 58, 99, 0.12);
      color: inherit;
      font-family: inherit;
      font-size: 11px;
      line-height: 1;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0;

      &:hover {
         background: rgba(11, 58, 99, 0.24);
      }
      &:focus-visible {
         outline: none;
         box-shadow: 0 0 0 3px rgba(64, 152, 239, 0.18);
      }
   }
`;

// Botão pílula "Mais/Menos filtros".
export const TogglePill = styled.button`
   height: 32px;
   padding: 0 12px;
   border-radius: 999px;
   border: 1px solid ${BORDER_STRONG};
   background: #fff;
   color: #2f7bd8;
   font-family: inherit;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   display: inline-flex;
   align-items: center;
   gap: 6px;

   &:hover {
      background: #f0f6fe;
   }
   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(64, 152, 239, 0.18);
   }
`;

// Link de ação no rodapé do card (Limpar filtros).
export const LinkAction = styled.button`
   height: 40px;
   padding: 0 12px;
   border: 0;
   border-radius: 10px;
   background: transparent;
   color: #2f7bd8;
   font-family: inherit;
   font-size: 14px;
   font-weight: 700;
   cursor: pointer;

   &:hover:not(:disabled) {
      background: #f0f6fe;
   }
   &:disabled {
      color: #b0b0b0;
      cursor: not-allowed;
   }
   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(64, 152, 239, 0.18);
   }
`;

export const MutedText = styled.span`
   font-size: 12px;
   font-weight: 600;
   color: #8a8a8a;
`;

// ─── KPIs do cabeçalho de resultados ─────────────────────────────────────────
export const KpiPill = styled.span`
   display: inline-flex;
   align-items: baseline;
   gap: 6px;
   padding: 5px 12px;
   border-radius: 999px;
   font-size: 12px;
   font-weight: 600;
   background: ${({ $tone }) =>
      $tone === 'warn' ? 'rgba(242,175,41,0.16)' : '#f4f4f4'};
   color: ${({ $tone }) => ($tone === 'warn' ? '#7d5a00' : '#555')};

   strong {
      font-weight: 800;
      font-variant-numeric: tabular-nums;
   }
`;

export const SortWrap = styled.div`
   display: inline-flex;
   align-items: center;
   gap: 8px;

   label {
      font-size: 12px;
      font-weight: 700;
      color: #8a8a8a;
   }

   select {
      height: 36px;
      border-radius: 10px;
      border: 1px solid ${BORDER_STRONG};
      background: #fff;
      color: #111;
      font-family: inherit;
      font-size: 13px;
      font-weight: 600;
      padding: 0 10px;
      cursor: pointer;

      &:focus {
         outline: none;
         border-color: #4098ef;
         box-shadow: 0 0 0 3px rgba(64, 152, 239, 0.18);
      }
   }
`;

// ─── Rodapé / paginação ──────────────────────────────────────────────────────
export const ListFoot = styled.div`
   display: flex;
   align-items: center;
   gap: 12px;
   flex-wrap: wrap;
   padding: 14px 16px;
`;

export const PageButton = styled.button`
   min-width: 36px;
   height: 36px;
   padding: 0 10px;
   border-radius: 10px;
   border: 1px solid ${BORDER_STRONG};
   font-family: inherit;
   font-size: 13px;
   font-weight: ${({ $current }) => ($current ? 800 : 600)};
   cursor: pointer;
   background: ${({ $current }) => ($current ? '#373737' : '#fff')};
   color: ${({ $current }) => ($current ? '#fff' : '#111')};

   &:disabled {
      color: #b0b0b0;
      cursor: not-allowed;
      background: #fff;
   }
   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(64, 152, 239, 0.18);
   }
`;

export const EmptyBox = styled.div`
   padding: 40px 16px;
   text-align: center;
   color: #8a8a8a;

   .title {
      font-size: 14px;
      font-weight: 700;
      color: #555;
   }
   .desc {
      font-size: 12.5px;
      margin-top: 4px;
   }
`;
